import { useMemo } from "react";
import JsonLd from "../../../components/feature/JsonLd";

const SITE_URL = import.meta.env.VITE_SITE_URL ?? "https://ltfsourcing.com";

export default function HomeSchemas() {
  const organizationSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": ["Organization", "ProfessionalService"],
      "@id": `${SITE_URL}/#organization`,
      name: "LTF Sourcing",
      description:
        "LTF Sourcing is a global procurement partner specializing in product sourcing, wholesale supply across 40+ categories, and overstock inventory solutions — serving businesses across 8+ countries.",
      url: SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/vite.svg`,
        caption: "LTF Sourcing logo",
      },
      email: "info@ltfsourcing.com",
      priceRange: "$$",
      areaServed: {
        "@type": "AdministrativeArea",
        name: "Global",
      },
      knowsAbout: [
        "Product Sourcing",
        "Wholesale Supply",
        "Inventory Solutions",
        "Overstock Liquidation",
        "Supplier Verification",
        "Procurement Outsourcing",
        "Supply Chain Management",
        "Logistics Coordination",
      ],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "LTF Sourcing Services",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Product Sourcing",
              description:
                "Global supplier discovery, price negotiation, sample coordination, and factory audits. Verified supplier options matched within a few business days.",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Wholesale Supply",
              description:
                "Direct manufacturer and distributor access across 40+ product categories with competitive bulk pricing and flexible minimum order quantities.",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Inventory Solutions",
              description:
                "Overstock and liquidation marketplace connecting surplus inventory sellers with pre-qualified bulk buyers.",
            },
          },
        ],
      },
      contactPoint: {
        "@type": "ContactPoint",
        email: "info@ltfsourcing.com",
        contactType: "customer service",
        availableLanguage: "English",
        hoursAvailable: {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "09:00",
          closes: "18:00",
        },
      },
      sameAs: [],
    }),
    []
  );

  const webSiteSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: "LTF Sourcing",
      url: SITE_URL,
      publisher: { "@id": `${SITE_URL}/#organization` },
      description:
        "End-to-end procurement partner — Product Sourcing, Wholesale Supply, and Inventory Solutions.",
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${SITE_URL}/?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    }),
    []
  );

  const faqSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How long does it take to source a product?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "After you submit your brief, our team works to identify verified supplier options within a few business days. Custom or highly specialized products may take longer — typically 5–10 business days for matching, with sample production at 10–20 days and bulk production at 30–60 days from approval depending on the supplier.",
          },
        },
        {
          "@type": "Question",
          name: "Is there a minimum order quantity for sourcing requests?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "No hard minimum — our Product Sourcing pillar works with businesses of all sizes. Smaller quantities may carry slightly higher per-unit costs, but our team always presents the most cost-effective options and flags volume thresholds that unlock better pricing.",
          },
        },
        {
          "@type": "Question",
          name: "Can you handle rush or urgent sourcing needs?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "We'll always do our best to accommodate time-sensitive requests. Reach out with your deadline and we'll be upfront about what's realistically achievable — we won't rush a supplier match at the expense of quality or proper vetting.",
          },
        },
        {
          "@type": "Question",
          name: "How are wholesale suppliers verified before I'm connected to them?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Every supplier in our Wholesale Supply network goes through a vetting process before we make an introduction. This includes business license verification, background checks, quality system review, and ongoing performance monitoring.",
          },
        },
        {
          "@type": "Question",
          name: "What product categories are available through wholesale?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Our Wholesale Supply pillar currently covers 40+ product categories — including consumer goods, electronics, apparel, industrial supplies, and more. We're actively expanding our network.",
          },
        },
        {
          "@type": "Question",
          name: "What happens if a wholesale supplier delivers defective goods?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Our process includes a Pre-Shipment QC step designed to catch issues before anything ships. If problems arise after delivery, LTF Sourcing will act as your advocate — helping manage supplier disputes and working toward a resolution.",
          },
        },
        {
          "@type": "Question",
          name: "How does listing my overstock inventory work?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Submit your inventory details — product type, condition, quantity, and target price. Our team then assesses market demand and works to connect you with relevant bulk buyers from our network.",
          },
        },
        {
          "@type": "Question",
          name: "How quickly can overstock inventory be cleared?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Timelines vary depending on product category, condition, and current buyer demand. Some listings move within a couple of weeks; others take longer. We'll give you an honest assessment after reviewing your inventory.",
          },
        },
        {
          "@type": "Question",
          name: "What condition is overstock merchandise typically in?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Most inventory is brand new — surplus from overproduction, canceled orders, or seasonal changeovers. Open-box, refurbished, or cosmetically blemished items are clearly labeled. All condition details are fully disclosed before any purchase commitment.",
          },
        },
        {
          "@type": "Question",
          name: "Does LTF Sourcing handle international shipping and customs?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes — logistics support is included in both Product Sourcing and Wholesale Supply. We coordinate freight options, help with customs documentation, and work with brokers to keep things moving across our active markets.",
          },
        },
        {
          "@type": "Question",
          name: "Which countries does LTF Sourcing serve?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "We currently have active operations across 8+ countries and are expanding our reach. Reach out with your location and we'll confirm whether we can support your needs.",
          },
        },
      ],
    }),
    []
  );

  const servicesSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "LTF Sourcing — Core Service Pillars",
      description:
        "Three core procurement capabilities from LTF Sourcing: Product Sourcing, Wholesale Supply, and Inventory Solutions.",
      url: SITE_URL,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          item: {
            "@type": "Service",
            name: "Product Sourcing",
            url: `${SITE_URL}/#product-sourcing`,
            description:
              "Global supplier discovery and matching, price negotiation, sample coordination, and factory audits. Verified supplier options delivered within a few business days.",
            provider: { "@type": "Organization", name: "LTF Sourcing" },
            serviceType: "Procurement",
            areaServed: "Global",
          },
        },
        {
          "@type": "ListItem",
          position: 2,
          item: {
            "@type": "Service",
            name: "Wholesale Supply",
            url: `${SITE_URL}/#wholesale-supply`,
            description:
              "Bulk pricing across 40+ product categories with direct manufacturer and distributor access, multi-supplier order consolidation, and flexible minimum order quantities.",
            provider: { "@type": "Organization", name: "LTF Sourcing" },
            serviceType: "Wholesale",
            areaServed: "Global",
          },
        },
        {
          "@type": "ListItem",
          position: 3,
          item: {
            "@type": "Service",
            name: "Inventory Solutions",
            url: `${SITE_URL}/#inventory-solutions`,
            description:
              "Overstock and liquidation marketplace connecting buyers and sellers of surplus goods. Timelines vary by category and demand.",
            provider: { "@type": "Organization", name: "LTF Sourcing" },
            serviceType: "Inventory Management",
            areaServed: "Global",
          },
        },
      ],
    }),
    []
  );

  const breadcrumbSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: SITE_URL,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "About LTF Sourcing",
          item: `${SITE_URL}/#about`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Product Sourcing",
          item: `${SITE_URL}/#product-sourcing`,
        },
        {
          "@type": "ListItem",
          position: 4,
          name: "Wholesale Supply",
          item: `${SITE_URL}/#wholesale-supply`,
        },
        {
          "@type": "ListItem",
          position: 5,
          name: "Inventory Solutions",
          item: `${SITE_URL}/#inventory-solutions`,
        },
        {
          "@type": "ListItem",
          position: 6,
          name: "How It Works",
          item: `${SITE_URL}/#how-it-works`,
        },
        {
          "@type": "ListItem",
          position: 7,
          name: "Overstock Deals",
          item: `${SITE_URL}/#overstock`,
        },
        {
          "@type": "ListItem",
          position: 8,
          name: "Testimonials",
          item: `${SITE_URL}/#testimonials`,
        },
        {
          "@type": "ListItem",
          position: 9,
          name: "FAQ",
          item: `${SITE_URL}/#faq`,
        },
        {
          "@type": "ListItem",
          position: 10,
          name: "Contact Us",
          item: `${SITE_URL}/#contact`,
        },
      ],
    }),
    []
  );

  const webPageSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${SITE_URL}/#webpage`,
      url: SITE_URL,
      name: "LTF Sourcing | Product Sourcing, Wholesale Supply & Inventory Solutions",
      description:
        "Global procurement partner — product sourcing from verified suppliers, wholesale supply across 40+ categories, and overstock inventory solutions. Serving 75+ businesses across 8+ countries.",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#organization` },
      primaryImageOfPage: {
        "@type": "ImageObject",
        url: "https://public.readdy.ai/ai/img_res/56b243467172c321ea30fde7453831bf.jpg",
        caption: "LTF Sourcing — global product sourcing and wholesale supply warehouse",
      },
      hasPart: [
        {
          "@type": "WebPageElement",
          name: "About LTF Sourcing",
          url: `${SITE_URL}/#about`,
          cssSelector: "#about",
          description:
            "Company background, mission, and the founding principles behind LTF Sourcing\u2019s three-pillar procurement model.",
        },
        {
          "@type": "WebPageElement",
          name: "Product Sourcing",
          url: `${SITE_URL}/#product-sourcing`,
          cssSelector: "#product-sourcing",
          description:
            "Global supplier discovery and matching with verified options delivered within a few business days. Price negotiation, sample coordination, and factory audits included.",
        },
        {
          "@type": "WebPageElement",
          name: "Wholesale Supply",
          url: `${SITE_URL}/#wholesale-supply`,
          cssSelector: "#wholesale-supply",
          description:
            "Direct manufacturer and distributor access across 40+ product categories. Competitive bulk pricing with flexible minimum order quantities.",
        },
        {
          "@type": "WebPageElement",
          name: "Inventory Solutions",
          url: `${SITE_URL}/#inventory-solutions`,
          cssSelector: "#inventory-solutions",
          description:
            "Overstock and liquidation marketplace for surplus goods. Connecting sellers with a growing network of pre-qualified bulk buyers.",
        },
        {
          "@type": "WebPageElement",
          name: "How It Works",
          url: `${SITE_URL}/#how-it-works`,
          cssSelector: "#how-it-works",
          description:
            "Step-by-step breakdown of the procurement process across all three pillars: Product Sourcing, Wholesale Supply, and Inventory Solutions.",
        },
        {
          "@type": "WebPageElement",
          name: "Overstock Deals",
          url: `${SITE_URL}/#overstock`,
          cssSelector: "#overstock",
          description:
            "Browse available overstock and liquidation inventory. Request a bulk purchase quote or list your own surplus stock.",
        },
        {
          "@type": "WebPageElement",
          name: "Testimonials",
          url: `${SITE_URL}/#testimonials`,
          cssSelector: "#testimonials",
          description:
            "Client feedback and reviews from businesses using LTF Sourcing for procurement and inventory management.",
        },
        {
          "@type": "WebPageElement",
          name: "FAQ",
          url: `${SITE_URL}/#faq`,
          cssSelector: "#faq",
          description:
            "Frequently asked questions covering Product Sourcing timelines, Wholesale Supply vetting, Inventory Solutions clearance, and international logistics.",
        },
        {
          "@type": "WebPageElement",
          name: "Contact Us",
          url: `${SITE_URL}/#contact`,
          cssSelector: "#contact",
          description:
            "Reach out to LTF Sourcing\u2019s procurement specialists for sourcing requests, wholesale inquiries, or inventory listing assistance.",
        },
      ],
    }),
    []
  );

  return (
    <>
      <JsonLd id="schema-organization" schema={organizationSchema} />
      <JsonLd id="schema-website" schema={webSiteSchema} />
      <JsonLd id="schema-faq" schema={faqSchema} />
      <JsonLd id="schema-services" schema={servicesSchema} />
      <JsonLd id="schema-breadcrumb" schema={breadcrumbSchema} />
      <JsonLd id="schema-webpage" schema={webPageSchema} />
    </>
  );
}